import React, { useState } from 'react';
import { useStyles, cx } from '../../../lib/styles';
import theme, { Brand } from '../../../lib/theme';
import Button from '../Button';
import TypeformEmbed from '../TypeformEmbed';
import Typography from '../Typography';
import BlockUpdates from '../../../behaviour/BlockUpdates';

import heroStyles from './Hero_styles';

/** A CTA for a hero. */
interface CTA {
  /** Whether the CTA should show as disabled. */
  disabled?: boolean;

  /** The text of the CTA button. */
  text: string;

  /** The click-through URL of the CTA. */
  url: string;
}

export interface HeroProps {
  /** The brand to use for colouring the hero card. */
  brand?: Brand;

  /** The title to display in the hero. */
  title: string;

  /** The subtitle to display in the hero. */
  subtitle?: string;

  /** The background image URL to display. */
  imageURL?: string;

  /** Any logo image URL to display at the top left of the hero. */
  logoURL?: string;

  /** The description (plain content) to display in the hero. */
  description?: string;

  /** Whether to split all the CTAs into equal width buttons. */
  ctaColumns?: boolean;

  /** The custom content to display at the bottom of the hero. */
  children?: React.ReactNode;

  /** The list of CTAs to show inside the hero. */
  ctas?: CTA[];

  /** Any smaller terms content to show at the bottom of the hero. */
  terms?: string;

  /** The event listener for listening to click on CTAs inside the hero. */
  onClick?: (url: string) => any;

  /** The transparent colour used for the background overlay */
  overlayColor?: string;
}

const isTypeformURL = (url: string) => url ? url.includes('typeform.com') : false;

const { colors } = theme;

/**
 * A hero card that can have CTAs or content.
 */
export const Hero = (props: HeroProps) => {
  const {
    brand = 'sas',
    title,
    subtitle,
    description,
    ctaColumns,
    children,
    ctas = [],
    logoURL,
    terms,
    onClick,
    overlayColor = colors[brand || 'sas'].primary,
  } = props;

  const [formRef, setFormRef] = useState<any>(undefined);
  const styles = useStyles(heroStyles(props));

  return (
    <div className={cx('Hero', styles.container)}>
      <Typography color={'white'}>
        <div className={styles.innerTop}>
          <div className={styles.heading}>
            {logoURL && (
              <img src={logoURL} width={176} className={styles.logo} />
            )}

            <div>
              {subtitle && <h3>{subtitle}</h3>}

              <h1 className={styles.title}>{title}</h1>
            </div>
          </div>

          {description && (
            <h3
              className={styles.description}
              dangerouslySetInnerHTML={{ __html: description }}
            />
          )}
        </div>
      </Typography>

      <div className={styles.innerChildren}>
        {children}

        <div className={styles.ctas}>
          {ctas.map(({ url, text, disabled = false }) => (
            <div className={styles.cta} key={text}>
              {isTypeformURL(url) && (
                <BlockUpdates
                  block
                  render={() => (
                    <TypeformEmbed
                      popup
                      autoOpen={false}
                      url={url}
                      hideHeaders
                      hideFooter
                      onRef={(formRef: any) => formRef && setFormRef(formRef)}
                    />
                  )}
                />
              )}

              <Button
                disabled={disabled}
                brand={brand}
                href={url}
                rel={'nofollow'}
                block={ctaColumns}
                onClick={event => {
                  if (onClick) onClick(url);
                  if (formRef) {
                    event.preventDefault();
                    formRef.open();

                    return false;
                  }

                  return true;
                }}
                type={'submit'}
                color={'secondary'}
              >
                {text}
              </Button>
            </div>
          ))}
        </div>

        {terms && (
          <Typography color={'white'} className={styles.terms}>
            <small dangerouslySetInnerHTML={{ __html: terms }} />
          </Typography>
        )}
      </div>
    </div>
  );
};

export default Hero;
